import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';
import { collection, doc, getDocs, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase';
import trackingService from '../../../../services/trackingService';
import ContactListStep from './SendEventSteps/ContactListStep';
import SubscriptionSelectionStep from './SendSubscriptionSteps/SubscriptionSelectionStep';
import EmailTemplateStep from './SendEventSteps/EmailTemplateStep';
import SubscriptionReviewStep from './SendSubscriptionSteps/SubscriptionReviewStep';
import { formatDate } from '../../../../utils/dateUtils';

// Interface for selected contacts
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
}

// Interface for subscription email template - local to this component
interface SubscriptionEmailTemplate {
  subject: string;
  body: string;
  includeCalendarInvite: boolean;
  includeSubscriptionDetails: boolean;
}

// Define the Subscription interface that matches EmailTemplateStep expectations
interface SubscriptionTier {
  id: string;
  name: string;
  description: string;
  price: number;
  interval: 'monthly' | 'annual';
  features: string[];
  isPublic: boolean;
  position: number;
  maxMembers?: number;
  trialDays?: number;
  status: 'active' | 'archived' | 'draft';
  coverImageUrl?: string;
  benefits?: string[];
  created?: Date;
  modified?: Date;
}

// Props interface
interface SendSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  contactsToInvite: string[];
  organizationId: string;
}

const SendSubscriptionDialog: React.FC<SendSubscriptionDialogProps> = ({
  open,
  onClose,
  contactsToInvite,
  organizationId,
}) => {
  // State management
  const [activeStep, setActiveStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedContacts, setSelectedContacts] = useState<string[]>(contactsToInvite);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionTier | null>(null);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<SubscriptionTier[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionTier[]>([]);
  const [emailTemplate, setEmailTemplate] = useState<SubscriptionEmailTemplate>({
    subject: '',
    body: '',
    includeCalendarInvite: false,
    includeSubscriptionDetails: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState<boolean>(false);
  const [sendingProgress, setSendingProgress] = useState<number>(0);
  const [isSingleContact, setIsSingleContact] = useState<boolean>(false);
  const [showAllSubscriptions, setShowAllSubscriptions] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Steps for the stepper
  const steps = isSingleContact 
    ? ['Select Subscription', 'Email Template', 'Review & Send'] 
    : ['Select Contacts', 'Select Subscription', 'Email Template', 'Review & Send'];

  // Fetch data when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedSubscription(null);
      setError(null);
      fetchSubscriptions();
      
      // Initialize email template with defaults
      setEmailTemplate({
        subject: 'Invitation to Subscribe',
        body: 'We\'d like to invite you to subscribe to our service.',
        includeCalendarInvite: false,
        includeSubscriptionDetails: true,
      });
      
      // Set initial contact IDs from props
      if (contactsToInvite.length > 0) {
        fetchSelectedContacts();
        
        // If only one contact is preselected, we're in single-contact mode
        if (contactsToInvite.length === 1) {
          setIsSingleContact(true);
          // Skip to subscription selection for single contact
          setActiveStep(0);
        }
      }
    }
  }, [open, organizationId, contactsToInvite]);

  // Fetch subscriptions from Firestore
  const fetchSubscriptions = async () => {
     setLoading(true);
     try {
        // Try to fetch from subscriptions collection
        const subscriptionsRef = collection(db, 'subscriptionTiers');

        // Query subscriptions for this organization
        const q = query(
           subscriptionsRef,
           where('organizationId', '==', organizationId),
           where('status', '==', 'active')
        );
        const subscriptionsSnapshot = await getDocs(q);

        if (subscriptionsSnapshot.docs.length > 0) {
           const subscriptionsData = processSubscriptionsData(subscriptionsSnapshot.docs);
           setSubscriptions(subscriptionsData);
        } else {
           // Use placeholder data for testing
           setSubscriptions([
              {
                 id: 'sub1',
                 name: 'Basic Membership',
                 description: 'Access to basic features and content',
                 price: 9.99,
                 interval: 'monthly',
                 features: ['Basic access', 'Community forum', 'Monthly newsletter'],
                 isPublic: true,
                 position: 1,
                 status: 'active',
                 coverImageUrl: 'https://source.unsplash.com/featured/?membership,basic',
                 benefits: ['Support our mission', 'Stay informed', 'Join our community'],
              },
              {
                 id: 'sub2',
                 name: 'Premium Membership',
                 description: 'Full access to all premium features and exclusive content',
                 price: 19.99,
                 interval: 'monthly',
                 features: [
                    'All basic features',
                    'Exclusive content',
                    'Priority support',
                    'Early access to new features',
                 ],
                 isPublic: true,
                 position: 2,
                 status: 'active',
                 coverImageUrl: 'https://source.unsplash.com/featured/?membership,premium',
                 benefits: ['Get exclusive access', 'Priority support', 'Shape our future'],
              },
              {
                 id: 'sub3',
                 name: 'Annual Membership',
                 description: 'Save with our annual subscription plan',
                 price: 99.99,
                 interval: 'annual',
                 features: [
                    'All premium features',
                    '2 months free',
                    'Special annual gift',
                    'Voting rights on new features',
                 ],
                 isPublic: true,
                 position: 3,
                 status: 'active',
                 coverImageUrl: 'https://source.unsplash.com/featured/?membership,annual',
                 benefits: ['Save with annual billing', 'Receive annual perks', 'Maximum support'],
              },
           ]);
        }
     } catch (error) {
        console.error('Error fetching subscriptions:', error);
        setError('Failed to load subscription options. Please try again.');
     } finally {
        setLoading(false);
     }
  };

  // Process subscriptions data from Firestore
  const processSubscriptionsData = (docs: any[]) => {
     return docs.map((doc) => {
        const data = doc.data();

        // Generate an image if none exists
        let coverImageUrl = data.coverImageUrl || data.imageUrl || '';
        if (!coverImageUrl) {
           // Default subscription images by tier type
           const tierTypes = ['basic', 'standard', 'premium', 'pro', 'enterprise'];
           const tierType = data.name?.toLowerCase().includes('premium')
              ? 'premium'
              : data.name?.toLowerCase().includes('pro')
                ? 'pro'
                : data.name?.toLowerCase().includes('enterprise')
                  ? 'enterprise'
                  : data.name?.toLowerCase().includes('basic')
                    ? 'basic'
                    : 'standard';
           coverImageUrl = `https://source.unsplash.com/featured/?membership,${tierType}`;
        }

        return {
           id: doc.id,
           name: data.name || 'Unnamed Subscription',
           description: data.description || '',
           price: data.price || 0,
           interval: data.interval || 'monthly',
           features: Array.isArray(data.features) ? data.features : [],
           isPublic: data.isPublic ?? true,
           position: data.position || 0,
           maxMembers: data.maxMembers,
           trialDays: data.trialDays,
           status: data.status || 'active',
           coverImageUrl,
           benefits: Array.isArray(data.benefits) ? data.benefits : [],
           created: data.created?.toDate(),
           modified: data.modified?.toDate(),
        };
     });
  };

  // Fetch selected contacts details
  const fetchSelectedContacts = async () => {
     setLoading(true);
     try {
        const contacts: Contact[] = [];

        for (const contactId of contactsToInvite) {
           const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
           const contactSnap = await getDoc(contactRef);

           if (contactSnap.exists()) {
              const data = contactSnap.data();
              contacts.push({
                 id: contactSnap.id,
                 firstName: data.firstName || '',
                 lastName: data.lastName || '',
                 email: data.email || '',
                 phone: data.phone || '',
                 company: data.company || '',
                 title: data.title || '',
                 tags: Array.isArray(data.tags) ? data.tags : [],
              });
           }
        }

        setFilteredContacts(contacts);
        setSelectedContacts(contactsToInvite);
     } catch (error) {
        console.error('Error fetching contacts:', error);
        setError('Failed to load contact information. Please try again.');
     } finally {
        setLoading(false);
     }
  };

  // Fetch all contacts
  const fetchAllContacts = async () => {
     setLoading(true);
     try {
        const contactsRef = collection(db, `organizations/${organizationId}/contacts`);
        const contactsSnapshot = await getDocs(contactsRef);

        const contacts: Contact[] = contactsSnapshot.docs.map((doc) => {
           const data = doc.data();
           return {
              id: doc.id,
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              email: data.email || '',
              phone: data.phone || '',
              company: data.company || '',
              title: data.title || '',
              tags: data.tags || [],
           };
        });

        setFilteredContacts(contacts);
     } catch (error) {
        console.error('Error fetching contacts:', error);
        setError('Failed to load contacts. Please try again.');
     } finally {
        setLoading(false);
     }
  };

  // Helper function to get Contact objects from IDs
  const getSelectedContactsObjects = () => {
     return filteredContacts.filter((contact) => selectedContacts.includes(contact.id));
  };

  // Handle next step
  const handleNext = () => {
     // Validate current step
     if (
        activeStep === (isSingleContact ? 0 : 1) &&
        !selectedSubscription &&
        selectedSubscriptions.length === 0
     ) {
        // Subscription selection step - require subscription selection
        setError('Please select a subscription to continue');
        return;
     }

     setError(null);
     setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle back step
  const handleBack = () => {
     setError(null);
     setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle contact selection
  const handleContactsChange = (contactIds: string[]) => {
     setSelectedContacts(contactIds);
  };

  // Handle subscription selection
  const handleSubscriptionSelect = (subscription: SubscriptionTier) => {
     setSelectedSubscription(subscription);
     setSelectedSubscriptions([subscription]);
     setShowAllSubscriptions(false);

     // Update the email template subject and body with the selected subscription info
     setEmailTemplate((prev) => ({
        ...prev,
        subject: `Join {{organizationName}} - ${subscription.name} Membership Invitation`,
        body: `
Dear {{firstName}},

On behalf of {{organizationName}}, we are pleased to extend a formal invitation for you to join us as a ${subscription.name} member.

The event will take place on Friday, April 11, 2025, from 11:00 to 18:00, at Online.

About the event: ${subscription.description}

Your presence would be greatly appreciated. Please RSVP by clicking the link below.

**Membership Details:**
• **Plan**: ${subscription.name}
• **Cost**: $${subscription.price.toFixed(2)}/${subscription.interval}

**Benefits Include:**
${subscription.benefits?.map((benefit) => `• ${benefit}`).join('\n') || subscription.features?.map((feature) => `• ${feature}`).join('\n') || ''}

By becoming a member, you'll gain access to exclusive resources, networking opportunities, and much more. We believe your participation will be valuable to our community.

To complete your subscription or for more information, please click the button below.

We look forward to welcoming you.

Warm regards,
{{organizationName}} Team
      `,
     }));
  };

  // Handle multiple subscriptions selection
  const handleMultipleSubscriptionsSelect = (subscriptions: SubscriptionTier[]) => {
     setSelectedSubscriptions(subscriptions);

     if (subscriptions.length === 0) {
        // No subscriptions selected
        setSelectedSubscription(null);
        setShowAllSubscriptions(false);

        // Reset email template
        setEmailTemplate((prev) => ({
           ...prev,
           subject: 'Invitation to Subscribe',
           body: "We'd like to invite you to subscribe to our service.",
        }));
     } else if (subscriptions.length === 1) {
        // Single subscription selected
        setSelectedSubscription(subscriptions[0]);
        setShowAllSubscriptions(false);

        // Update email template with single subscription
        handleSubscriptionSelect(subscriptions[0]);
     } else {
        // Multiple subscriptions selected
        setSelectedSubscription(null);
        setShowAllSubscriptions(true);

        // Update email template with all selected subscriptions
        const subscriptionDetailsList = subscriptions
           .map(
              (sub) =>
                 `**${sub.name}**
${sub.description}

• **Price**: $${sub.price.toFixed(2)}/${sub.interval}

• **Benefits**:
${sub.benefits?.map((benefit) => `  - ${benefit}`).join('\n') || sub.features?.map((feature) => `  - ${feature}`).join('\n') || ''}
`
           )
           .join('\n\n');

        setEmailTemplate((prev) => ({
           ...prev,
           subject: `Join {{organizationName}} - Membership Opportunities`,
           body: `
Dear {{firstName}},

On behalf of {{organizationName}}, we are pleased to extend a formal invitation for you to join us as a valuable member.

The event will take place on Friday, April 11, 2025, from 11:00 to 18:00, at Online.

About the event: ${subscriptions.length} subscription options available

Your presence would be greatly appreciated. Please RSVP by clicking the link below.

**Available Membership Options:**

${subscriptionDetailsList}

By becoming a member, you'll gain access to exclusive resources, networking opportunities, and much more tailored to your selected plan. We believe your participation will be valuable to our community.

To view all subscription options and complete your registration, please click the button below.

We look forward to welcoming you.

Warm regards,
{{organizationName}} Team
        `,
        }));
     }
  };

  // Handle email template changes from EmailTemplateStep component
  const handleEmailTemplateChange = (template: any) => {
     setEmailTemplate({
        subject: template.subject,
        body: template.body,
        includeCalendarInvite: template.includeCalendarInvite,
        includeSubscriptionDetails: template.includeTripDetails || false,
     });
  };

  // Send invitation emails
  const handleSend = async () => {
     setSending(true);
     setSendingProgress(0);

     try {
        const selectedContacts = getSelectedContactsObjects();
        let progress = 0;

        // In a real application, send emails to each contact
        for (const contact of selectedContacts) {
           try {
              // Track this email in our analytics system
              const subscriptionName = selectedSubscription
                 ? selectedSubscription.name
                 : selectedSubscriptions.length > 0
                   ? `Multiple Subscriptions (${selectedSubscriptions.length})`
                   : 'Subscription Invitation';

              const subscriptionId = selectedSubscription
                 ? selectedSubscription.id
                 : selectedSubscriptions.length > 0
                   ? selectedSubscriptions.map((sub) => sub.id).join(',')
                   : 'multiple';

              await trackingService.trackContactEmail(
                 contact.id,
                 organizationId,
                 'subscription',
                 subscriptionId,
                 subscriptionName,
                 {
                    emailTemplate: {
                       subject: emailTemplate.subject,
                       includeCalendarInvite: emailTemplate.includeCalendarInvite,
                       includeSubscriptionDetails: emailTemplate.includeSubscriptionDetails,
                    },
                    contact: {
                       firstName: contact.firstName,
                       lastName: contact.lastName,
                       email: contact.email,
                    },
                 }
              );
           } catch (trackingError) {
              console.error('Error tracking email:', trackingError);
              // Continue with sending even if tracking fails
           }

           // Simulate email sending delay
           await new Promise((resolve) => setTimeout(resolve, 500));

           // Update progress
           progress += 1;
           setSendingProgress(Math.floor((progress / selectedContacts.length) * 100));
        }

        // Simulate final processing
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Close dialog after successful send
        setTimeout(() => {
           setSending(false);
           onClose();

           // Trigger refresh of contacts data to update analytics
           const refreshEvent = new CustomEvent('contactsUpdated');
           window.dispatchEvent(refreshEvent);
        }, 500);
     } catch (error) {
        console.error('Error sending invitation emails:', error);
        setError('Failed to send invitation emails. Please try again.');
        setSending(false);
     }
  };

  // Render the current step
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        if (isSingleContact) {
          // Skip to subscription selection for single contact
          return (
            <SubscriptionSelectionStep
              subscriptions={subscriptions}
              selectedSubscription={selectedSubscription}
              selectedSubscriptions={selectedSubscriptions}
              onSubscriptionSelect={handleSubscriptionSelect}
              onMultipleSubscriptionsSelect={handleMultipleSubscriptionsSelect}
              allowMultipleSelection={true}
              loading={loading}
            />
          );
        } else {
          return (
            <ContactListStep
              contacts={filteredContacts}
              selectedContacts={selectedContacts}
              onContactsChange={handleContactsChange}
              loading={loading}
              onRefresh={fetchAllContacts}
              error={error}
            />
          );
        }
      case 1:
        // Second step: Subscription selection (in multiple contacts mode) or Email template (in single contact mode)
        if (!isSingleContact) {
          return (
            <SubscriptionSelectionStep
              subscriptions={subscriptions}
              selectedSubscription={selectedSubscription}
              selectedSubscriptions={selectedSubscriptions}
              onSubscriptionSelect={handleSubscriptionSelect}
              onMultipleSubscriptionsSelect={handleMultipleSubscriptionsSelect}
              allowMultipleSelection={true}
              loading={loading}
            />
          );
        } else {
          return (
            <EmailTemplateStep
              template={{
                subject: emailTemplate.subject,
                body: emailTemplate.body,
                includeCalendarInvite: emailTemplate.includeCalendarInvite,
                includeTripDetails: emailTemplate.includeSubscriptionDetails
              }}
              onChange={handleEmailTemplateChange}
              selectedEvent={getSelectedEventData()}
              selectedContacts={getSelectedContactsObjects()}
            />
          );
        }
      case 2:
        // Third step: Email template (in multiple contacts mode) or Review (in single contact mode)
        if (!isSingleContact) {
          return (
            <EmailTemplateStep
              template={{
                subject: emailTemplate.subject,
                body: emailTemplate.body,
                includeCalendarInvite: emailTemplate.includeCalendarInvite,
                includeTripDetails: emailTemplate.includeSubscriptionDetails
              }}
              onChange={handleEmailTemplateChange}
              selectedEvent={getSelectedEventData()}
              selectedContacts={getSelectedContactsObjects()}
            />
          );
        } else {
          return (
            <SubscriptionReviewStep
              contacts={getSelectedContactsObjects()}
              subscription={selectedSubscription}
              emailTemplate={emailTemplate}
              onSend={handleSend}
              sending={sending}
              sendingProgress={sendingProgress}
            />
          );
        }
      case 3:
        // Fourth step: Review (in multiple contacts mode)
        return (
          <SubscriptionReviewStep
            contacts={getSelectedContactsObjects()}
            subscription={selectedSubscription}
            emailTemplate={emailTemplate}
            onSend={handleSend}
            sending={sending}
            sendingProgress={sendingProgress}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  // Get selected event data for email template
  const getSelectedEventData = () => {
    if (showAllSubscriptions) {
      // Return combined data for all selected subscriptions
      return {
        eventName: 'Multiple Subscription Options',
        eventDate: new Date().toISOString(),
        eventDescription: `${selectedSubscriptions.length} subscription options available`,
        location: 'Online'
      };
    } else if (selectedSubscription) {
      // Return data for single selected subscription
      return {
        eventName: selectedSubscription.name,
        eventDate: new Date().toISOString(),
        eventDescription: selectedSubscription.description,
        location: 'Online'
      };
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={sending}
    >
      <DialogTitle>
        Send Subscription Invitation
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {getStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        {sending ? (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sending invitations... {sendingProgress}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={sendingProgress} 
              sx={{ width: '100%', height: 8, borderRadius: 4 }}
            />
          </Box>
        ) : (
          <>
            <Button onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button 
              onClick={handleBack} 
              disabled={activeStep === 0 || sending}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button 
                variant="contained"
                color="primary"
                onClick={handleSend}
                disabled={sending || 
                  (isSingleContact && !selectedSubscription && selectedSubscriptions.length === 0) ||
                  (!isSingleContact && selectedContacts.length === 0) ||
                  (!selectedSubscription && selectedSubscriptions.length === 0)
                }
              >
                Send Invitations
              </Button>
            ) : (
              <Button 
                variant="contained" 
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && !isSingleContact && selectedContacts.length === 0) ||
                  (activeStep === (isSingleContact ? 0 : 1) && !selectedSubscription && selectedSubscriptions.length === 0)
                }
              >
                Next
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendSubscriptionDialog;
